import request from '@/services/api';
import { type EmotionAnalysis } from '@/services/modules/emotionService';

export interface ChildChatRequest {
  message: string
  childAge: number
  interests?: string
  learningLevel?: string
}

export interface ChildChatResponse {
  response: string
  timestamp: string
  isSafeForChildren: boolean
  relatedKnowledgePoints: string
  emotionAnalysis?: EmotionAnalysis // 添加情感分析结果
}

export interface ChildChatStreamChunk {
  text: string
  isComplete: boolean
}

// 添加打断消息类型
export interface InterruptMessage {
  type: 'interrupt'
}

// 修改智能对话响应类型
export type SmartDialogResponse = 
  | { type: 'text'; text: string }
  | { type: 'complete' }
  | { type: 'error'; message: string }
  | { type: 'interrupt' };

export interface IntegratedStreamChunk {
  text: string;
  isComplete: boolean;
  audioData?: string; // 音频数据（Base64）
}

export interface ChatSession {
  id: number
  sessionName: string
  createdAt: string
  updatedAt: string
}

export interface ChatMessage {
  id: number
  sessionId: number
  role: 'user' | 'assistant'
  content: string
  createdAt: string
}

/**
 * 会话流式聊天 - 支持上下文记忆
 */
export const streamChatWithChildInSession = async (
  sessionId: number,
  data: ChildChatRequest, 
  onTextChunk: (chunk: ChildChatStreamChunk) => void,
  onError?: (error: string) => void
): Promise<void> => {
  const url = `/api/child/session/${sessionId}/chat/stream`;
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    
    if (!reader) {
      throw new Error('No reader available');
    }
    
    let buffer = '';
    let currentEvent = '';
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      
      // 处理 SSE 格式的数据
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        
        if (trimmedLine.startsWith('event:')) {
          currentEvent = trimmedLine.substring(6).trim();
        } else if (trimmedLine.startsWith('data:')) {
          try {
            const dataStr = trimmedLine.substring(5).trim();
            const eventData = JSON.parse(dataStr);
            
            if (currentEvent === 'text' && eventData.text !== undefined) {
              onTextChunk({
                text: eventData.text,
                isComplete: eventData.isComplete || false
              });
            } else if (currentEvent === 'complete') {
              console.log('【DEBUG】childChat.ts 收到 complete 事件');
              onTextChunk({
                text: '',
                isComplete: true
              });
            } else if (currentEvent === 'error') {
              if (onError) {
                onError(eventData.message || '服务暂时不可用，请稍后再试');
              }
              break;
            }
          } catch (e) {
            console.error('解析SSE数据失败:', e);
            if (onError) {
              onError('数据解析错误');
            }
            break;
          }
        }
      }
    }
  } catch (error) {
    console.error('会话流式聊天请求失败:', error);
    if (onError) {
      onError('网络连接失败，请检查网络后重试');
    }
  }
}

export const createChatSession = (sessionName?: string): Promise<{ id: number }> => {
  return request.post('/child/session', { sessionName }).then(response => {
    // 处理ResponseDTO格式: { code, message, data }
    if (response && response.data) {
      return response.data;
    }
  });
}

export const getUserChatSessions = (): Promise<ChatSession[]> => {
  return request.get('/child/sessions').then(response => {
    // 处理ResponseDTO格式: { code, message, data }
    if (response && response.data) {
      return response.data;
    }
  });
}

export const getChatHistory = (sessionId: number): Promise<ChatMessage[]> => {
  return request.get(`/child/session/${sessionId}/history`).then(response => {
    // 处理ResponseDTO格式: { code, message, data }
    if (response && response.data) {
      return response.data;
    }
  });
}

export const integratedStreamChatWithVoice = async (
  data: ChildChatRequest, 
  onTextChunk: (chunk: ChildChatStreamChunk) => void,
  onAudioChunk?: (audioData: string) => void,
  onVoiceComplete?: () => void,
  onError?: (error: string) => void
): Promise<void> => {
  const url = '/api/integrated/chat/voice/stream';
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    
    if (!reader) {
      throw new Error('No reader available');
    }
    
    let buffer = '';
    let currentEvent = '';
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      
      // 处理 SSE 格式的数据
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        
        if (trimmedLine.startsWith('event:')) {
          currentEvent = trimmedLine.substring(6).trim();
        } else if (trimmedLine.startsWith('data:')) {
          try {
            const dataStr = trimmedLine.substring(5).trim();
            
            if (currentEvent === 'text' && dataStr.includes('"text"')) {
              const eventData = JSON.parse(dataStr);
              onTextChunk({
                text: eventData.text,
                isComplete: eventData.isComplete || false
              });
            } else if (currentEvent === 'voice_data') {
              // 接收到音频数据块
              if (onAudioChunk) {
                onAudioChunk(dataStr);
              }
            } else if (currentEvent === 'complete') {
              onTextChunk({
                text: '',
                isComplete: true
              });
            } else if (currentEvent === 'voice_complete') {
              // 语音合成完成
              console.log('【DEBUG】childChat.ts 收到 voice_complete 事件');
              if (onVoiceComplete) {
                onVoiceComplete();
              }
            } else if (currentEvent === 'error' || currentEvent === 'voice_error') {
              if (onError) {
                onError(dataStr || '服务暂时不可用，请稍后再试');
              }
              break;
            }
          } catch (e) {
            console.error('解析集成流式数据失败:', e);
            if (onError) {
              onError('数据解析错误');
            }
            break;
          }
        }
      }
    }
  } catch (error: unknown) {
    console.error('【DEBUG】集成流式请求失败:', error);
    if (error instanceof Error) {
      console.error('【DEBUG】集成流式错误详情:', error.message);
    } else {
      console.error('【DEBUG】集成流式错误详情:', String(error));
    }
    if (onError) {
      onError('网络连接失败，请检查网络后重试');
    }
  }
}

export const integratedStreamChatWithVoiceInSession = async (
  sessionId: number,
  data: ChildChatRequest, 
  onTextChunk: (chunk: ChildChatStreamChunk) => void,
  onAudioChunk?: (audioData: string) => void,
  onVoiceComplete?: () => void,
  onError?: (error: string) => void
): Promise<void> => {
  const url = `/api/integrated/session/${sessionId}/chat/voice/stream`;
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    
    if (!reader) {
      throw new Error('No reader available');
    }
    
    let buffer = '';
    let currentEvent = '';
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      
      // 处理 SSE 格式的数据
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        
        if (trimmedLine.startsWith('event:')) {
          currentEvent = trimmedLine.substring(6).trim();
        } else if (trimmedLine.startsWith('data:')) {
          try {
            const dataStr = trimmedLine.substring(5).trim();
            //console.log('语音SSE数据:', dataStr);
            if (currentEvent === 'text' && dataStr.includes('"text"')) {
              const eventData = JSON.parse(dataStr);
              onTextChunk({
                text: eventData.text,
                isComplete: eventData.isComplete || false
              });
            } else if (currentEvent === 'voice_data') {
              // 接收到音频数据块
              if (onAudioChunk) {
                onAudioChunk(dataStr);
              }
            } else if (currentEvent === 'complete') {
              onTextChunk({
                text: '',
                isComplete: true
              });
            } else if (currentEvent === 'voice_complete') {
              // 语音合成完成
              console.log('【DEBUG】childChat.ts 收到 voice_complete 事件');
              if (onVoiceComplete) {
                onVoiceComplete();
              }
            } else if (currentEvent === 'error' || currentEvent === 'voice_error') {
              if (onError) {
                onError(dataStr || '服务暂时不可用，请稍后再试');
              }
              break;
            }
          } catch (e) {
            console.error('解析集成流式数据失败:', e);
            if (onError) {
              onError('数据解析错误');
            }
            break;
          }
        }
      }
    }
  } catch (error: unknown) {
    console.error('【DEBUG】集成流式请求失败（带会话）:', error);
    if (error instanceof Error) {
      console.error('【DEBUG】集成流式错误详情（带会话）:', error.message);
    } else {
      console.error('【DEBUG】集成流式错误详情（带会话）:', String(error));
    }
    if (onError) {
      onError('网络连接失败，请检查网络后重试');
    }
  }
}